import { useCallback, useEffect, useState } from 'react';

type ContextSelectionOptions = {
  getInitialValue: () => string | null;
  onPersist: (value: string | null) => void;
  normalize?: (value: string | null) => string | null;
};

type ContextSelectionState = { value: string | null; setValue: (value: string | null) => void };

export const useContextSelection = ({
  getInitialValue,
  onPersist,
  normalize,
}: ContextSelectionOptions): ContextSelectionState => {
  const [value, setValue] = useState<string | null>(() => getInitialValue());

  const setValueNormalized = useCallback(
    (next: string | null) => {
      const resolved = normalize ? normalize(next) : next;
      setValue(resolved);
    },
    [normalize],
  );

  useEffect(() => {
    onPersist(value);
  }, [onPersist, value]);

  useEffect(() => {
    const handleSignout = (event: Event): void => {
      const detail = (event as CustomEvent<{ clearSelections?: boolean }>).detail;
      if (detail.clearSelections !== true) return;
      setValueNormalized(null);
    };
    const handleOrgRemoved = (): void => {
      setValueNormalized(null);
    };
    window.addEventListener('flowqa-signout', handleSignout);
    window.addEventListener('flowqa-org-removed', handleOrgRemoved);
    return () => {
      window.removeEventListener('flowqa-signout', handleSignout);
      window.removeEventListener('flowqa-org-removed', handleOrgRemoved);
    };
  }, [setValueNormalized]);

  return { value, setValue: setValueNormalized };
};
